// WO-GF-001-P1B-S37 — ONE AIRPORT-PLACE RULE, shared by every map surface that draws `/api/transport/geo`.
//
// WHY THIS IS A MODULE AND NOT A HELPER INSIDE ONE COMPONENT
// ---------------------------------------------------------
// The feed serves ONE physical airport as THREE fare-tier rows:
//
//   yvr-vancouver-airport            49.1950, -123.1802
//   yvr-vancouver-airport-express    49.1950, -123.1802
//   yvr-vancouver-airport-executive  49.1967, -123.1815
//
// S35 · F204 first collapsed them with a COORDINATE key (`toFixed(4)`, i.e. ~11m of tolerance) on the
// assumption that fare tiers of one place share a coordinate. S36 · F275 measured that assumption false:
// the EXECUTIVE tier sits **190m** from the other two, 190m ≫ 11m, so it survived the key and rendered a
// second "airport" that was really the same airport again. On the R1a map the two pins landed on the
// identical pixel box [775,406,26,30] — one teardrop drawn exactly on top of another, which is why the
// founder's report was "no airport pins render at all" rather than "there are too many".
//
// The fix F275 shipped is that IDENTITY IS THE PLACE, derived from the name with the fare-tier suffix
// stripped — not the coordinate, and not a hardcoded list of airports. That fix was applied to the
// KeylessMap path (the R1a selector) and to nothing else. `TransportMountainDirectory` draws the same
// feed through `PortMap` and had NO dedupe at all, so it kept rendering all three tiers. That is the
// second-implementation failure this repo has now paid for three times (F192's two marker files, F191's
// two bookability chips, and now this), so the rule lives HERE, once, and every caller imports it.
// (DEAD-SELECTOR-CLEANUP-20260925: TransportMountainDirectory is deleted; the R1a selector map is the live caller.)
//
// NOTHING IS INVENTED AND NOTHING REAL IS SUPPRESSED: two genuinely different airports have different
// names, so both survive the key and both render. If the served network ever gains a second airport it
// appears on every map automatically, with no edit to this file.

/** A served stop is an airport candidate when its NAME says so — the feed carries no type column. */
export const AIRPORT_STOP_NAME_RE = /airport|yvr/i;

/** The fare-tier suffix — "(EXPRESS)" / "(EXECUTIVE)" — is a product, not a place. */
const TIER_SUFFIX_RE = /\s*\((express|executive)\)\s*$/i;

/** The stop's PLACE, as a display name: the served name with its fare tier removed. */
export function airportPlaceName(name: string): string {
  return name.replace(TIER_SUFFIX_RE, "").trim();
}

/** The stop's PLACE, as an identity key. Case-folded so "YVR" and "Yvr" are one place, not two. */
export function airportPlaceKey(name: string): string {
  return airportPlaceName(name).toUpperCase();
}

/** Is this served stop an airport at all? */
export function isAirportStop<T extends { name: string }>(stop: T): boolean {
  return AIRPORT_STOP_NAME_RE.test(stop.name);
}

/**
 * The airport stops of a served stop list, ONE ROW PER PLACE, in served order (the first tier served for
 * a place wins — every tier of a place carries the same airport, so which row survives is arbitrary and
 * the surviving NAME is tier-stripped either way).
 *
 * Callers map the result to their own marker shape; use `airportPlaceName(s.name)` for the label so the
 * pin names the airport rather than whichever tier happened to sort first.
 */
export function airportStopsByPlace<T extends { name: string }>(stops: readonly T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const s of stops) {
    if (!isAirportStop(s)) continue;
    const place = airportPlaceKey(s.name);
    if (seen.has(place)) continue;
    seen.add(place);
    out.push(s);
  }
  return out;
}
